import { useCallback, useEffect, useMemo, useState } from 'react'
import { KpiTile } from '../components/KpiTile.js'
import { SlideOver } from '../components/SlideOver.js'
import {
  addBoardRow, boardPackExportUrl, boardPackPptxUrl, createBoardPack, deleteBoardPack, deleteBoardRow, fetchBoardCandidates, fetchBoardPack,
  fetchBoardSettings, listBoardPacks, saveBoardSettings, updateBoardRow,
  type BoardCandidate, type BoardFamily, type BoardPackListItem, type BoardRowInput,
} from '../lib/api.js'
import {
  BOARD_SECTIONS, SECTION_TARGETS, SECTION_TITLES, compactGBP, currentMonth, tcvCell,
  type BoardPackView, type BoardRow, type BoardSection, type BookingRow,
} from '@shared/boardPack'
import { formatGBP, formatGBPCompact } from '@shared/money'
import { formatUKDate } from '@shared/dates'
import { StackedBarChart, gbpAxis } from '../lib/charts.js'

/**
 * Decision 205 — the monthly board pack (Mark, 2026-09-07).
 *
 * The board slide, rebuilt from the portal every month instead of from a
 * spreadsheet: three hand-picked lists (booked · pipeline / favourable ·
 * notable new), each over a whole-population total, and a PowerPoint export on
 * the Wave9 template. (Decision 245: no stage-weighted pipeline here — the one
 * weighted forecast is the Wave9 Hub's ARR Cockpit.)
 *
 * Whole page = new business, every partner. What a person types (Notes,
 * Sites, Live by, a manual row's own figures) is stored; everything else is
 * the quote, live. A new month carries last month's pipeline and notable
 * picks forward while they are still open.
 */

const SECTION_HINT: Record<BoardSection, string> = {
  pipeline: 'open quotes you expect to land — the total is every open quote',
  notable: 'new opportunities worth the board\'s attention — the total is everything first sent this month',
}

export function BoardPack({ admin }: { admin: boolean }) {
  const [packs, setPacks] = useState<BoardPackListItem[] | null>(null)
  const [packId, setPackId] = useState<string | null>(null)
  const [pack, setPack] = useState<BoardPackView | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [newMonth, setNewMonth] = useState(currentMonth())
  const [picker, setPicker] = useState<BoardSection | null>(null)
  const [manual, setManual] = useState<BoardSection | null>(null)
  const [settingsOpen, setSettingsOpen] = useState(false)

  const loadList = useCallback(async (preferId?: string | null) => {
    try {
      const r = await listBoardPacks()
      setPacks(r.packs)
      const id = preferId ?? packId ?? r.packs[0]?.id ?? null
      setPackId(id && r.packs.some((p) => p.id === id) ? id : (r.packs[0]?.id ?? null))
    } catch (e) { setError((e as Error).message) }
  }, [packId])

  const loadPack = useCallback(async (id: string) => {
    try { setPack((await fetchBoardPack(id)).pack); setError(null) }
    catch (e) { setError((e as Error).message) }
  }, [])

  useEffect(() => { void loadList() }, [])     // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (packId) void loadPack(packId); else setPack(null) }, [packId, loadPack])

  const refresh = () => { if (packId) void loadPack(packId) }

  const create = async () => {
    setBusy(true); setError(null)
    try {
      const r = await createBoardPack(newMonth)
      setNotice(r.created
        ? (r.carried ? `Pack created — ${r.carried} pipeline / notable pick${r.carried === 1 ? '' : 's'} carried forward from the previous month.` : 'Pack created.')
        : 'That month already has a pack — opened it.')
      await loadList(r.id)
      setPackId(r.id)
    } catch (e) { setError((e as Error).message) } finally { setBusy(false) }
  }

  const remove = async () => {
    if (!packId || !pack) return
    if (!window.confirm(`Delete the ${pack.monthLabel} board pack? The picks and notes go with it; quotes are untouched.`)) return
    setBusy(true)
    try { await deleteBoardPack(packId); setPackId(null); await loadList(null) }
    catch (e) { setError((e as Error).message) } finally { setBusy(false) }
  }

  const s = pack?.summary

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Board pack</h1>
          <p>
            The monthly “Opportunity Pipeline Overview” slide — new business, every partner.
            Pick the highlights; the totals are the whole company.
          </p>
        </div>
        <div className="toolbar">
          {packs && packs.length > 0 && (
            <select className="select" value={packId ?? ''} onChange={(e) => setPackId(e.target.value)} aria-label="Board pack month" data-testid="pack-select">
              {packs.map((p) => <option key={p.id} value={p.id}>{p.monthLabel}{p.rows ? ` · ${p.rows} rows` : ''}</option>)}
            </select>
          )}
          <input className="input" type="month" value={newMonth} onChange={(e) => setNewMonth(e.target.value)} aria-label="New pack month" style={{ width: 150 }} />
          <button className="btn btn--primary" disabled={busy || !/^\d{4}-\d{2}$/.test(newMonth)} onClick={() => void create()} data-testid="new-pack">
            + Pack for month
          </button>
          {pack && (
            <a className="btn" href={boardPackPptxUrl(pack.id)} download data-testid="export-pptx">⬇ Export PowerPoint</a>
          )}
          {pack && (
            <a className="btn" href={boardPackExportUrl(pack.id)} download data-testid="export-xlsx" title="The month's bookings, 13 months of history and the highlighted rows, as the group board pack's capture workbook">⬇ Export workbook (.xlsx)</a>
          )}
          {admin && <button className="btn" onClick={() => setSettingsOpen(true)} data-testid="open-settings">Product Line families</button>}
          {admin && pack && <button className="btn btn--ghost" disabled={busy} onClick={() => void remove()}>Delete pack</button>}
        </div>
      </div>

      {error && <p className="pill pill--stop" style={{ marginBottom: 12 }}>{error}</p>}
      {notice && (
        <div className="banner" style={{ marginBottom: 12 }}>
          <span>{notice}</span>
          <button className="btn btn--ghost btn--sm" onClick={() => setNotice(null)}>Dismiss</button>
        </div>
      )}
      {packs && packs.length === 0 && (
        <div className="card"><div className="card__body">
          <strong>No board packs yet.</strong>
          <p className="muted" style={{ margin: '6px 0 0' }}>Choose a month above and create the first pack. Each later month starts from the one before.</p>
        </div></div>
      )}

      {pack && s && (
        <>
          <div className="tiles" data-testid="board-tiles">
            <KpiTile accent label={`Booked · ${pack.monthLabel}`} value={compactGBP(pack.bookings.totals.tcv)} sub={`${pack.bookings.totals.n} orders — the bookings slide below`} />
            <KpiTile label="Open pipeline" value={compactGBP(s.pipelineTcv)} sub={`${pack.population.pipeline.n} open quotes`} />
            <KpiTile label="Pipeline picks" value={compactGBP(pack.totals.pipeline.highlightedTcv)}
              sub={`${pack.totals.pipeline.highlightedN} of ${SECTION_TARGETS.pipeline} picked`} />
            <KpiTile label="Sent this month" value={compactGBP(s.notableTcv)} sub={`${pack.population.notable.n} quotes first sent · ${pack.totals.notable.highlightedN} of ${SECTION_TARGETS.notable} picked`} />
            <KpiTile label="Combined TCV" value={compactGBP(s.combinedTcv)}
              sub={s.split.map((p) => `${p.label} ${Math.round(p.share * 100)}%`).join(' · ')} />
          </div>

          {BOARD_SECTIONS.map((sec) => (
            <SectionCard key={sec} section={sec} pack={pack} onPick={() => setPicker(sec)} onManual={() => setManual(sec)} onChanged={refresh} onError={setError} />
          ))}

          <BookingsCard pack={pack} />

          <p className="muted small">
            Sites and Live by are typed per row and kept with the pack; customer, family, TCV, term and stage are read live from the quote.
            A manual row (marked *) is something not yet quoted — its TCV is an estimate and the export says so.
            {pack.updatedAt && <> Last change {formatUKDate(pack.updatedAt)}{pack.updatedBy ? ` by ${pack.updatedBy}` : ''}.</>}
          </p>
        </>
      )}

      {picker && pack && (
        <Picker pack={pack} section={picker} onClose={() => setPicker(null)} onAdded={() => { refresh() }} />
      )}
      {manual && pack && (
        <ManualRow pack={pack} section={manual} onClose={() => setManual(null)} onAdded={() => { setManual(null); refresh() }} />
      )}
      {settingsOpen && (
        <Settings onClose={() => { setSettingsOpen(false); void loadList(); refresh() }} />
      )}
    </>
  )
}

// ---------------------------------------------------------------- bookings (decision 254)

const FOLD_AT = 12

/**
 * The new-business bookings slide: the month's bookings (newest first,
 * totalled; folds to the biggest past FOLD_AT — #204) and the rolling
 * 13-month TCV history with ARR / one-off / count under the bars. Read-only:
 * nothing here is picked or typed, it is the whole population.
 */
function BookingsCard({ pack }: { pack: BoardPackView }) {
  const b = pack.bookings
  const [showAll, setShowAll] = useState(false)
  const rows = useMemo(() => {
    if (showAll || b.rows.length <= FOLD_AT) return b.rows
    return [...b.rows].sort((x, y) => y.tcv - x.tcv).slice(0, FOLD_AT)
  }, [b.rows, showAll])
  const folded = b.rows.length - rows.length
  const prev = b.history[b.history.length - 2]
  const uncovered = b.history.filter((m) => !m.covered).length
  // Decision 256: the bar is the TCV — one-off + ARR (year 1) + the later years of the term.
  const chart = b.history.map((m) => ({ label: m.label, month: m.month, muted: m.manual, parts: (m.covered ? [m.oneOff, m.arr, Math.max(0, Math.round((m.tcv - m.arr - m.oneOff) * 100) / 100)] : [0, 0, 0]) as [number, number, number] }))
  const roll = b.rolling
  const rollNote = roll.covered < roll.months ? ` · ${roll.covered} of ${roll.months} months on record` : ''
  const handEntered = b.history.filter((m) => m.manual)
  const bookedOn = (r: BookingRow) => formatUKDate(r.acceptedAt)
  return (
    <div className="card" style={{ marginBottom: 16 }} data-testid="section-bookings">
      <div className="card__head">
        <div>
          <h2>New business bookings</h2>
          <span className="muted small">{b.label} — the second slide of the export. Not picked: this is everything.</span>
        </div>
      </div>
      <div className="card__body">
        <div className="tiles" data-testid="bookings-tiles">
          <KpiTile accent label={`Bookings · ${pack.monthLabel}`} value={String(b.totals.n)} sub={prev ? `${prev.n} in ${prev.label}` : ''} />
          <KpiTile label="TCV booked" value={compactGBP(b.totals.tcv)} sub={prev && prev.covered ? `${compactGBP(prev.tcv)} in ${prev.label}` : ''} />
          <KpiTile label="ARR booked" value={compactGBP(b.totals.arr)} sub={prev && prev.covered ? `${compactGBP(prev.arr)} in ${prev.label}` : ''} />
          <KpiTile label="One-off booked" value={compactGBP(b.totals.oneOff)} sub={prev && prev.covered ? `${compactGBP(prev.oneOff)} in ${prev.label}` : ''} />
        </div>
        <div className="tiles" data-testid="bookings-rolling" style={{ marginTop: 8 }}>
          <KpiTile label={`Rolling 12-month TCV · to ${b.history[b.history.length - 1]?.label ?? ''}`} value={compactGBP(roll.total.tcv)} sub={`${roll.total.n} bookings · ${compactGBP(roll.total.arr)} ARR · ${compactGBP(roll.total.oneOff)} one-off${rollNote}`} />
          <KpiTile label="Average month" value={compactGBP(roll.average.tcv)} sub={`${roll.average.n} bookings · ${compactGBP(roll.average.arr)} ARR · ${compactGBP(roll.average.oneOff)} one-off`} />
        </div>

        <h3 style={{ margin: '14px 0 4px' }}>Rolling {b.history.length}-month history — TCV booked per month = one-off + ARR (year 1) + later years</h3>
        {handEntered.length > 0 && (
          <p className="muted small" style={{ margin: '0 0 6px' }}>
            {handEntered.map((m) => m.label).join(', ')}: hand-entered month {handEntered.length === 1 ? 'summary' : 'summaries'} from the old reporting (marked †), not computed from records.
          </p>
        )}
        {uncovered > 0 && (
          <p className="muted small" style={{ margin: '0 0 6px' }}>
            No records before {b.history.find((m) => m.covered && !m.manual)?.label ?? '—'} (the HubSpot backfill starts there) — earlier months read as no data, not £0, unless a hand-entered summary covers them.
          </p>
        )}
        <StackedBarChart wide data={chart} names={['One-off', 'ARR (year 1)', 'Later years']} totalName="TCV booked" format={(v) => formatGBP(v)} axisFormat={gbpAxis} />
        <div className="table-wrap" style={{ marginTop: 8 }}>
          <table className="small" data-testid="bookings-history">
            <thead>
              <tr><th /> {b.history.map((m) => <th key={m.month} className="num">{m.label}{m.manual ? ' †' : ''}</th>)}</tr>
            </thead>
            <tbody>
              <tr><td className="muted">TCV</td>{b.history.map((m) => <td key={m.month} className="num">{m.covered ? formatGBPCompact(m.tcv) : <span className="muted">—</span>}</td>)}</tr>
              <tr><td className="muted">ARR</td>{b.history.map((m) => <td key={m.month} className="num">{m.covered ? formatGBPCompact(m.arr) : <span className="muted">—</span>}</td>)}</tr>
              <tr><td className="muted">One-off</td>{b.history.map((m) => <td key={m.month} className="num">{m.covered ? formatGBPCompact(m.oneOff) : <span className="muted">—</span>}</td>)}</tr>
              <tr><td className="muted">Bookings</td>{b.history.map((m) => <td key={m.month} className="num">{m.covered ? m.n : <span className="muted">—</span>}</td>)}</tr>
            </tbody>
          </table>
        </div>

        <h3 style={{ margin: '16px 0 4px' }}>Booked in {pack.monthLabel}</h3>
        <div className="table-wrap">
          <table data-testid="bookings-list">
            <thead>
              <tr><th>Customer</th><th>Details</th><th className="num">TCV</th><th className="num">ARR</th><th className="num">One-off</th><th className="num">Booked</th><th>Source</th></tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={7} className="muted">No new-business bookings this month.</td></tr>}
              {rows.map((r) => (
                <tr key={`${r.kind}-${r.id}`}>
                  <td><strong>{r.name}</strong>{r.title && r.title !== r.name && <div className="muted small">{r.title}</div>}</td>
                  <td>{r.family ?? <span className="muted">—</span>}</td>
                  <td className="num">{tcvCell(r.tcv, r.termMonths)}</td>
                  <td className="num">{formatGBPCompact(r.arr)}</td>
                  <td className="num">{formatGBPCompact(r.oneOff)}</td>
                  <td className="num">{bookedOn(r)}</td>
                  <td className="small muted">{r.kind === 'quote' ? <>{r.quoteNumber}{r.partnerName ? ` · ${r.partnerName}` : ''}</> : 'HubSpot booking'}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr data-testid="bookings-totals">
                <td colSpan={2}>
                  <strong>Total {b.totals.n} {b.totals.n === 1 ? 'booking' : 'bookings'}</strong>
                  {folded > 0 && <> <span className="muted small">— {folded} smaller not listed</span> <button className="btn btn--ghost btn--sm" onClick={() => setShowAll(true)}>Show all</button></>}
                  {showAll && b.rows.length > FOLD_AT && <> <button className="btn btn--ghost btn--sm" onClick={() => setShowAll(false)}>Show fewer</button></>}
                </td>
                <td className="num"><strong>{formatGBPCompact(b.totals.tcv)}</strong></td>
                <td className="num"><strong>{formatGBPCompact(b.totals.arr)}</strong></td>
                <td className="num"><strong>{formatGBPCompact(b.totals.oneOff)}</strong></td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------- sections

function SectionCard({ section, pack, onPick, onManual, onChanged, onError }: {
  section: BoardSection
  pack: BoardPackView
  onPick: () => void
  onManual: () => void
  onChanged: () => void
  onError: (m: string) => void
}) {
  const rows = pack.rows.filter((r) => r.section === section)
  const pop = pack.population[section]
  const tot = pack.totals[section]
  const isPipeline = section === 'pipeline'

  const move = async (row: BoardRow, dir: -1 | 1) => {
    const i = rows.indexOf(row)
    const other = rows[i + dir]
    if (!other) return
    try {
      // Swap sort keys; a tie (fresh rows share 0) is broken by giving them distinct values.
      const a = row.sort === other.sort ? row.sort + (dir > 0 ? 1 : -1) : other.sort
      const b = row.sort === other.sort ? row.sort : row.sort
      await updateBoardRow(pack.id, row.id, { sort: a })
      await updateBoardRow(pack.id, other.id, { sort: b })
      onChanged()
    } catch (e) { onError((e as Error).message) }
  }

  return (
    <div className="card" style={{ marginBottom: 16 }} data-testid={`section-${section}`}>
      <div className="card__head">
        <div>
          <h2>{SECTION_TITLES[section]}</h2>
          <span className="muted small">{SECTION_HINT[section]}</span>
        </div>
        <div className="toolbar">
          <span className="muted small" data-testid={`target-${section}`} style={{ marginRight: 8 }}>{rows.length} of {SECTION_TARGETS[section]} picks{rows.length < SECTION_TARGETS[section] ? ` — ${SECTION_TARGETS[section] - rows.length} more to pick` : rows.length > SECTION_TARGETS[section] ? ` — ${rows.length - SECTION_TARGETS[section]} over` : ''}</span>
          <button className="btn btn--sm" onClick={onPick} data-testid={`pick-${section}`}>+ Add quote</button>
          <button className="btn btn--sm btn--ghost" onClick={onManual} data-testid={`manual-${section}`}>+ Manual row</button>
        </div>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Type</th>
              <th>Details</th>
              <th className="num">TCV</th>
              {isPipeline && <th>Stage</th>}
              <th>Live by</th>
              <th>Notes</th>
              <th className="num">Sites</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={isPipeline ? 8 : 7} className="muted">Nothing picked yet — pick {SECTION_TARGETS[section]} quotes, or add a manual row.</td></tr>
            )}
            {rows.map((r, i) => (
              <RowLine key={r.id} row={r} pack={pack} isPipeline={isPipeline}
                onChanged={onChanged} onError={onError}
                onUp={i > 0 ? () => void move(r, -1) : undefined} onDown={i < rows.length - 1 ? () => void move(r, 1) : undefined} />
            ))}
          </tbody>
          <tfoot>
            <tr data-testid={`totals-${section}`}>
              <td colSpan={2}>
                <strong>{section === 'pipeline' ? 'Total open pipeline' : 'Total sent this month'}</strong>
                {' '}<span className="muted small">{pop.label}</span>
              </td>
              <td className="num"><strong>{formatGBPCompact(pop.tcv)}</strong><span className="muted small"> · {pop.n}</span></td>
              {isPipeline && <td />}
              <td colSpan={2} className="muted small">
                Highlighted rows {formatGBPCompact(tot.highlightedTcv)} · {tot.highlightedN}
              </td>
              <td className="num"><strong>{tot.highlightedSites}</strong></td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  )
}

function RowLine({ row, pack, isPipeline, onChanged, onError, onUp, onDown }: {
  row: BoardRow
  pack: BoardPackView
  isPipeline: boolean
  onChanged: () => void
  onError: (m: string) => void
  onUp?: () => void
  onDown?: () => void
}) {
  const [liveBy, setLiveBy] = useState(row.liveBy ?? '')
  const [notes, setNotes] = useState(row.notes ?? '')
  const [sites, setSites] = useState(row.sites == null ? '' : String(row.sites))
  useEffect(() => { setLiveBy(row.liveBy ?? ''); setNotes(row.notes ?? ''); setSites(row.sites == null ? '' : String(row.sites)) }, [row.liveBy, row.notes, row.sites])

  const save = async (patch: BoardRowInput) => {
    try { await updateBoardRow(pack.id, row.id, patch); onChanged() }
    catch (e) { onError((e as Error).message) }
  }
  const remove = async () => {
    try { await deleteBoardRow(pack.id, row.id); onChanged() }
    catch (e) { onError((e as Error).message) }
  }
  const stale = row.kind === 'quote' && row.status !== 'sent' && row.status !== 'viewed'

  return (
    <tr data-testid="board-row" data-kind={row.kind}>
      <td>
        <strong>{row.name}</strong>{row.kind === 'manual' && ' *'}
        <div className="muted small">
          {row.kind === 'quote' && <>{row.quoteNumber}{row.ownerName ? ` · ${row.ownerName}` : ''}</>}
          {row.kind === 'booking' && <>HubSpot booking{row.acceptedAt ? ` · ${formatUKDate(row.acceptedAt)}` : ''}</>}
          {row.kind === 'manual' && 'manual row — not yet quoted'}
          {stale && <> · <span className="pill pill--warn">{row.status}</span></>}
        </div>
      </td>
      <td>
        {row.family ?? <span className="muted">—</span>}
        {row.unmappedLines.length > 0 && (
          <div className="muted small" title="Map these Product Lines to a family under Product Line families">unmapped: {row.unmappedLines.join(', ')}</div>
        )}
      </td>
      <td className="num" title={formatGBPCompact(row.tcv)}>
        {tcvCell(row.tcv, row.termMonths)}{row.kind === 'manual' && <span className="muted small"> (est)</span>}
        {row.kind !== 'manual' && (row.oneOff > 0 || row.arr > 0) && (
          <div className="muted small">{formatGBPCompact(row.oneOff)} one-off · {formatGBPCompact(row.arr)} ARR</div>
        )}
      </td>
      {isPipeline && (
        <td>
          {row.kind === 'quote'
            ? <>{row.stageLabel ?? <span className="muted">no deal stage</span>}</>
            : <span className="muted small">—</span>}
        </td>
      )}
      <td>
        <input className="input" style={{ width: 110 }} value={liveBy} placeholder="e.g. Oct 26" aria-label="Live by"
          onChange={(e) => setLiveBy(e.target.value)} onBlur={() => { if (liveBy !== (row.liveBy ?? '')) void save({ liveBy }) }} />
      </td>
      <td>
        <textarea className="input" rows={2} style={{ width: '100%', minWidth: 220, resize: 'vertical' }} value={notes} aria-label="Notes"
          onChange={(e) => setNotes(e.target.value)} onBlur={() => { if (notes !== (row.notes ?? '')) void save({ notes }) }} />
      </td>
      <td className="num">
        <input className="input" type="number" min={0} style={{ width: 64 }} value={sites} aria-label="Sites"
          placeholder={row.sitesDerived == null ? '' : String(row.sitesDerived)}
          title={row.sitesDerived == null ? 'Type the site count' : `Blank = ${row.sitesDerived} from the quote's sites`}
          onChange={(e) => setSites(e.target.value)}
          onBlur={() => { const v = sites === '' ? null : Number(sites); if (v !== row.sites) void save({ sites: v }) }} />
      </td>
      <td style={{ whiteSpace: 'nowrap' }}>
        <button className="btn btn--ghost btn--sm" onClick={onUp} disabled={!onUp} aria-label="Move up">↑</button>
        <button className="btn btn--ghost btn--sm" onClick={onDown} disabled={!onDown} aria-label="Move down">↓</button>
        <button className="btn btn--ghost btn--sm" onClick={() => void remove()} aria-label="Remove row" title="Remove from the pack (the quote is untouched)">✕</button>
      </td>
    </tr>
  )
}

// ---------------------------------------------------------------- picker

function Picker({ pack, section, onClose, onAdded }: {
  pack: BoardPackView
  section: BoardSection
  onClose: () => void
  onAdded: () => void
}) {
  const [q, setQ] = useState('')
  const [rows, setRows] = useState<BoardCandidate[] | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [adding, setAdding] = useState<string | null>(null)
  const [added, setAdded] = useState<Set<string>>(new Set())

  useEffect(() => {
    const t = setTimeout(() => {
      fetchBoardCandidates(pack.id, section, q).then((r) => setRows(r.candidates)).catch((e: Error) => setErr(e.message))
    }, 200)
    return () => clearTimeout(t)
  }, [pack.id, section, q])

  const add = async (c: BoardCandidate) => {
    setAdding(c.id); setErr(null)
    try {
      await addBoardRow(pack.id, c.kind === 'quote' ? { section, quoteId: c.id } : { section, historicId: c.id })
      setAdded((s) => new Set(s).add(c.id))
      onAdded()
    } catch (e) { setErr((e as Error).message) } finally { setAdding(null) }
  }

  return (
    <SlideOver title={`Add to ${SECTION_TITLES[section]}`}
      subtitle={`Open new-business quotes, biggest first — pick ${SECTION_TARGETS[section]} for ${SECTION_TITLES[section].toLowerCase()}`}
      onClose={onClose}>
      <input className="input" style={{ width: '100%', marginBottom: 10 }} autoFocus placeholder="Search customer, title or quote number…" value={q}
        onChange={(e) => setQ(e.target.value)} aria-label="Search candidates" data-testid="picker-search" />
      {err && <p className="pill pill--stop">{err}</p>}
      {rows === null && <p className="muted">Loading…</p>}
      {rows && rows.length === 0 && <p className="muted">Nothing matches{q ? ` “${q}”` : ''} — or everything is already in this section.</p>}
      {rows && rows.length > 0 && (
        <div className="table-wrap">
          <table data-testid="picker-table">
            <tbody>
              {rows.map((c) => (
                <tr key={`${c.kind}:${c.id}`}>
                  <td>
                    <strong>{c.name}</strong>
                    <div className="muted small">
                      {c.kind === 'quote' ? <>{c.quoteNumber}{c.title ? ` · ${c.title}` : ''}</> : <>HubSpot booking · {c.title}</>}
                      {c.partnerName ? ` · ${c.partnerName}` : ''}
                    </div>
                  </td>
                  <td className="num">{formatGBPCompact(c.tcv)}</td>
                  <td className="muted small">
                    {c.stageLabel ?? c.status ?? ''}
                    {c.sites != null && c.sites > 0 && <> · {c.sites} site{c.sites === 1 ? '' : 's'}</>}
                  </td>
                  <td>
                    <button className="btn btn--sm btn--primary" disabled={adding === c.id || added.has(c.id)} onClick={() => void add(c)}>
                      {added.has(c.id) ? 'Added' : adding === c.id ? '…' : 'Add'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SlideOver>
  )
}

// ---------------------------------------------------------------- manual row

function ManualRow({ pack, section, onClose, onAdded }: {
  pack: BoardPackView
  section: BoardSection
  onClose: () => void
  onAdded: () => void
}) {
  const [name, setName] = useState('')
  const [family, setFamily] = useState('')
  const [tcv, setTcv] = useState('')
  const [term, setTerm] = useState('36')
  const [liveBy, setLiveBy] = useState('')
  const [notes, setNotes] = useState('')
  const [sites, setSites] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const families = useMemo(() => [...new Set(pack.rows.map((r) => r.family).filter((f): f is string => !!f))], [pack.rows])

  const submit = async () => {
    setBusy(true); setErr(null)
    try {
      await addBoardRow(pack.id, {
        section, name, family: family || null, tcv: tcv === '' ? null : Number(tcv), termMonths: term === '' ? null : Number(term),
        liveBy: liveBy || null, notes: notes || null, sites: sites === '' ? null : Number(sites),
      })
      onAdded()
    } catch (e) { setErr((e as Error).message) } finally { setBusy(false) }
  }

  const field = (label: string, el: JSX.Element) => (
    <label style={{ display: 'block', marginBottom: 10 }}><span className="muted small" style={{ display: 'block', marginBottom: 3 }}>{label}</span>{el}</label>
  )
  return (
    <SlideOver title={`Manual row — ${SECTION_TITLES[section]}`} subtitle="Something not yet quoted. Its TCV is an estimate and the slide marks it *" onClose={onClose}
      footer={<><button className="btn" onClick={onClose}>Cancel</button> <button className="btn btn--primary" disabled={busy || !name.trim() || tcv === ''} onClick={() => void submit()} data-testid="manual-save">Add row</button></>}>
      {err && <p className="pill pill--stop">{err}</p>}
      {field('Type (customer / opportunity)', <input className="input" style={{ width: '100%' }} value={name} onChange={(e) => setName(e.target.value)} autoFocus data-testid="manual-name" />)}
      {field('Details (family)', <><input className="input" list="board-families" style={{ width: '100%' }} value={family} onChange={(e) => setFamily(e.target.value)} placeholder="WaveConnect" />
        <datalist id="board-families">{families.map((f) => <option key={f} value={f} />)}</datalist></>)}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {field('Estimated TCV (£)', <input className="input" type="number" min={0} style={{ width: '100%' }} value={tcv} onChange={(e) => setTcv(e.target.value)} data-testid="manual-tcv" />)}
        {field('Term (months)', <input className="input" type="number" min={0} step={12} style={{ width: '100%' }} value={term} onChange={(e) => setTerm(e.target.value)} />)}
        {field('Live by', <input className="input" style={{ width: '100%' }} value={liveBy} onChange={(e) => setLiveBy(e.target.value)} placeholder="Through 2027" />)}
        {field('Sites', <input className="input" type="number" min={0} style={{ width: '100%' }} value={sites} onChange={(e) => setSites(e.target.value)} />)}
      </div>
      {field('Notes', <textarea className="input" rows={3} style={{ width: '100%' }} value={notes} onChange={(e) => setNotes(e.target.value)} />)}
    </SlideOver>
  )
}

// ---------------------------------------------------------------- settings

function Settings({ onClose }: { onClose: () => void }) {
  const [families, setFamilies] = useState<BoardFamily[] | null>(null)
  const [editable, setEditable] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    fetchBoardSettings().then((r) => { setFamilies(r.families); setEditable(r.editable) }).catch((e: Error) => setErr(e.message))
  }, [])

  const save = async () => {
    if (!families) return
    setBusy(true); setErr(null); setSaved(null)
    try {
      const r = await saveBoardSettings({
        families: families.map((f) => ({ name: f.name, boardFamily: f.boardFamily })),
      })
      setSaved(`Saved — ${r.familiesUpdated} Product Line families.`)
    } catch (e) { setErr((e as Error).message) } finally { setBusy(false) }
  }

  const familyNames = useMemo(() => [...new Set((families ?? []).map((f) => f.boardFamily).filter((f): f is string => !!f))], [families])

  return (
    <SlideOver title="Product Line families" subtitle="Which family each Product Line rolls up to on the slide" onClose={onClose}
      footer={<><button className="btn" onClick={onClose}>Close</button> {editable && <button className="btn btn--primary" disabled={busy} onClick={() => void save()} data-testid="settings-save">Save</button>}</>}>
      {err && <p className="pill pill--stop">{err}</p>}
      {saved && <p className="pill pill--ok">{saved}</p>}
      {!editable && families && <p className="banner"><strong>Read-only.</strong><span>Sign in with the admin key to change these.</span></p>}
      <h3 style={{ margin: '8px 0 6px' }}>Product Line families</h3>
      <p className="muted small" style={{ marginTop: 0 }}>
        The slide's “Details” column. A quote shows the families of its line items' Product Lines; a line with no family is flagged on the row.
      </p>
      {families && (
        <div className="table-wrap">
          <table data-testid="families-table">
            <thead><tr><th>Product Line</th><th>Family</th></tr></thead>
            <tbody>
              {families.map((f) => (
                <tr key={f.name}>
                  <td>{f.name}</td>
                  <td>
                    <input className="input" list="board-family-names" style={{ width: 180 }} value={f.boardFamily ?? ''} disabled={!editable}
                      placeholder="unmapped" aria-label={`Family for ${f.name}`}
                      onChange={(e) => setFamilies((fs) => (fs ?? []).map((x) => x.name === f.name ? { ...x, boardFamily: e.target.value || null } : x))} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <datalist id="board-family-names">{familyNames.map((f) => <option key={f} value={f} />)}</datalist>
        </div>
      )}
    </SlideOver>
  )
}
